import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { HeartHandshake, Building2, Briefcase } from "lucide-react";
import { COMPANY } from "@/lib/content";
import { EmailText } from "./EmailText";
import { photoSet } from "@/lib/img";

const LINES = ["Steady hands.", "Elevated care.", "Close to home."];
const ease = [0.22, 1, 0.36, 1];

const MaskedLine = ({ text, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${i === 1 ? "italic text-[#9B742A]" : ""}`} initial={{ y: "105%" }} animate={{ y: 0 }} transition={{ duration: 1.1, delay: 0.15 + i * 0.14, ease }}>
      {text}
    </motion.span>
  </span>
);

const HeroImage = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const spotX = useTransform(mx, [-0.5, 0.5], ["20%", "80%"]);
  const spotY = useTransform(my, [-0.5, 0.5], ["20%", "80%"]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative [perspective:1200px]" data-testid="hero-image-stage">
      <motion.div style={{ rotateX: rx, rotateY: ry }} initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.3, ease }} className="relative [transform-style:preserve-3d]">
        <div className="arch-frame relative aspect-[4/5] w-full overflow-hidden bg-[#EDF1E6] shadow-[0_40px_80px_-30px_rgba(21,79,73,0.45)]">
          <motion.img style={{ y: imgY, scale: 1.18 }} src="/images/hero.jpg" srcSet={photoSet("/images/hero.jpg", 1400)} sizes="(min-width: 1024px) 40vw, 100vw" fetchPriority="high" decoding="async" alt="Caregiver sharing a warm moment with an older adult at home" className="absolute inset-0 w-full h-full object-cover" />
          <motion.div aria-hidden className="absolute inset-0 mix-blend-soft-light" style={{ background: useTransform([spotX, spotY], ([x, y]) => `radial-gradient(circle at ${x} ${y}, rgba(255,240,200,0.55), transparent 55%)`) }} />
          <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#154F49]/45 via-transparent to-transparent" />
        </div>
        <div className="absolute -left-4 sm:-left-10 bottom-10 [transform:translateZ(60px)] bg-[#FBF8F1] rounded-2xl px-5 py-4 shadow-xl border border-[#CDA34F]/50 max-w-[230px]" data-testid="hero-floating-badge">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#9B742A] font-semibold">Serving</p>
          <p className="font-serif text-xl text-[#154F49] leading-tight mt-1">Maryland &amp; the D.C. Metro</p>
        </div>
        <div className="absolute -right-3 top-8 [transform:translateZ(80px)] w-24 h-24 rounded-full bg-[#154F49] text-[#FBF8F1] flex items-center justify-center seal-spin">
          <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" aria-hidden>
            <defs><path id="sealpath" d="M50,50 m-37,0 a37,37 0 1,1 74,0 a37,37 0 1,1 -74,0" /></defs>
            <text fontSize="9.5" letterSpacing="2.6" fill="#E2C372"><textPath href="#sealpath">CARE • STAFFING • CARE • STAFFING •</textPath></text>
          </svg>
          <span className="font-serif text-2xl italic">E</span>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section id="top" className="relative overflow-hidden grain" data-testid="hero-section">
    <div aria-hidden className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full bg-[#EDF1E6]" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-20 lg:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
      <div className="lg:col-span-7 space-y-8">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="text-xs font-semibold uppercase tracking-[0.28em] text-[#9B742A]">
          Home care &amp; healthcare staffing · Upper Marlboro, MD
        </motion.p>
        <h1 className="font-serif font-semibold text-[#154F49] leading-[0.95] tracking-tight text-[3.1rem] sm:text-7xl lg:text-[6.4rem]" data-testid="hero-heading">
          {LINES.map((l, i) => <MaskedLine key={l} text={l} i={i} />)}
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7, ease }} className="max-w-xl text-base sm:text-lg text-[#2C3B37] leading-relaxed">
          Elevion Care Network LLC connects families and healthcare facilities with credentialed RNs, LPNs, CNAs, GNAs, CMTs and Home Health Aides — matched to each person's care plan and setting.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85, ease }} className="grid grid-cols-1 sm:flex sm:flex-wrap gap-3" data-testid="hero-actions">
          <a href="#request-care" data-testid="hero-request-care-btn" className="btn-primary justify-center text-base px-7 py-4 shadow-[0_14px_30px_-12px_rgba(21,79,73,0.7)]"><HeartHandshake className="w-5 h-5" /> Request Care</a>
          <a href="#request-staff" data-testid="hero-request-staff-btn" className="btn-gold justify-center text-base px-7 py-4"><Building2 className="w-5 h-5" /> Request Staff</a>
          <a href="#apply-online" data-testid="hero-apply-btn" className="btn-ghost justify-center text-base px-7 py-4"><Briefcase className="w-5 h-5" /> Apply for a Job</a>
        </motion.div>
        <p className="text-sm text-[#465F57]">
          Call <a href={COMPANY.phoneHref} className="font-semibold text-[#154F49] underline underline-offset-4" data-testid="hero-call-link">{COMPANY.phoneDisplay}</a> or email <a href={`mailto:${COMPANY.email}`} className="font-semibold text-[#154F49] underline underline-offset-4"><EmailText /></a> to confirm coverage, scope and availability.
        </p>
      </div>
      <div className="lg:col-span-5 px-4 sm:px-10 lg:px-0"><HeroImage /></div>
    </div>
  </section>
);
